import { Modal, FormField, Select, AcknowledgeModal, ConfirmationModal } from '@percent/lemonade'
import { FormikProvider, useFormik } from 'formik'
import { object, string } from 'yup'
import styles from './EditUserRoleModal.module.scss'
import { useEffect, useState } from 'react'
import { useMutation, useTranslatedRole } from '@percent/cause-dashboard/common/hooks'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { useTranslation } from 'react-i18next'
import { IamGetRoles } from '@percent/cause-dashboard/services/types/iamResponses.types'

interface EditUserRoleModalProps {
  accountId: string
  role: {
    id: string
    displayName: string
  } | null
  email: string
  open: boolean
  close: (refetch: boolean) => void
  roles: IamGetRoles['roles']
}

enum EditRoleModalState {
  FORM,
  STATUS
}

export const EditUserRoleModal = ({ accountId, email, role, open, close, roles: allRoles }: EditUserRoleModalProps) => {
  const { t } = useTranslation()
  const { translateRole } = useTranslatedRole()
  const [isRoleSelected, setIsRoleSelected] = useState(false)
  const [selectedRole, setSelectedRole] = useState('')
  const [modalState, setModalState] = useState(EditRoleModalState.FORM)
  const [acknowledgeModalData, setAcknowledgeModalData] = useState({ title: '', description: '', result: '' })
  const { iamService } = useServices()

  const translatedCurrentRole = role ? translateRole(role) : null
  const roles =
    allRoles
      ?.map(r => {
        const translated = translateRole(r)
        return { label: translated.displayName, value: r.id, description: translated.description }
      })
      .filter(proposedRole => proposedRole.value !== role?.id) || []

  const [{ isLoading: isMutating }, { apiFunc: updateUserRoleAPIRequest }] = useMutation(
    iamService.patchRoles,
    _success => {
      setAcknowledgeModalData({
        title: t('typography.userManagement.editSuccessModal.title'),
        description: t('typography.userManagement.editSuccessModal.description', { email: email, role: selectedRole }),
        result: 'positive'
      })
      setModalState(EditRoleModalState.STATUS)
    },
    _error => {
      setAcknowledgeModalData({
        title: t('typography.userManagement.editFailureModal.title'),
        description: t('typography.userManagement.editFailureModal.description', { email: email }),
        result: 'negative'
      })
      setModalState(EditRoleModalState.STATUS)
    }
  )

  const roleId = role?.id ?? ''

  const formik = useFormik({
    initialValues: {
      roleId: roleId
    },
    validationSchema: () =>
      object().shape({
        roleId: string().required('Required')
      }),
    onSubmit: values => {
      updateUserRoleAPIRequest({
        accountId: accountId,
        roleId: values.roleId
      })
    }
  })

  const { touched, errors, handleSubmit, setFieldValue, resetForm } = formik

  useEffect(() => {
    if (!open) {
      setIsRoleSelected(false)
      resetForm()
      setSelectedRole('')
    }
  }, [resetForm, open])

  return (
    <Modal open={open} onClose={() => close(false)} data-testid="edit-user-role-modal">
      {modalState === EditRoleModalState.FORM && (
        <ConfirmationModal
          title={t('typography.userManagement.editUserRoleModal.title')}
          type="submit"
          loading={isMutating}
          disabled={!isRoleSelected}
          primaryButtonText={t('button.saveChanges')}
          primaryBtnTestId="button-save-changes"
          secondaryButtonText={t('button.cancel')}
          secondaryBtnTestId="button-cancel"
          handleClose={() => close(false)}
          handleSubmit={handleSubmit}
        >
          <form onSubmit={handleSubmit}>
            <FormikProvider value={formik}>
              <span className={styles.boldText} data-testid={'email'}>
                {email}
              </span>
              {translatedCurrentRole &&
                t('typography.userManagement.editUserRoleModal.description', {
                  role: `'${translatedCurrentRole.displayName}'`
                })}
              <div className={styles.editUserRoleModalFormFields} data-testid={'edit-role'}>
                <FormField
                  label={t('typography.userManagement.role')}
                  status={touched.roleId && errors.roleId ? 'danger' : 'default'}
                  statusMessage={errors.roleId}
                >
                  <Select
                    name="roleId"
                    data-testid="role-select"
                    placeholder={translatedCurrentRole?.displayName ?? t('typography.userManagement.selectRole')}
                    onChange={selection => {
                      setFieldValue('roleId', selection.value)
                      setIsRoleSelected(true)
                      setSelectedRole(selection.label)
                    }}
                    options={roles}
                  />
                </FormField>
              </div>
            </FormikProvider>
          </form>
        </ConfirmationModal>
      )}
      {modalState === EditRoleModalState.STATUS && (
        <AcknowledgeModal
          title={acknowledgeModalData.title}
          handleClose={() => close(acknowledgeModalData.result === 'positive')}
          description={acknowledgeModalData.description}
          result={acknowledgeModalData.result as 'positive' | 'negative'}
          buttonText={t('button.close')}
          buttonTestId="edit-role-acknowledge-modal-button"
          viewTestId="edit-role-acknowledge-modal-view"
        />
      )}
    </Modal>
  )
}
